import React, { useEffect, useState } from 'react';
import type { CrmEntry, RedshiftExportJob, RedshiftTarget } from '@atlas/shared';
import {
  DEFAULT_REDSHIFT_EXCLUDE_TABLES,
  DEFAULT_REDSHIFT_EXPORT_BUCKET,
  REDSHIFT_STANDARD_TABLES,
  REDSHIFT_STEMS_NOT_OFFERED,
  REDSHIFT_TARGET_LABELS,
  SOURCE_SYSTEMS,
  SOURCE_SYSTEM_LABELS,
  buildRedshiftExportPrefix,
  normalizeS3Prefix,
  parseTablePatterns,
  redshiftExportDestination,
  redshiftSchemaError,
  resolveRedshiftExportPrefix,
  resolveRedshiftMappingPrefix,
  tablePatternError,
  tablePatternMatches,
} from '@atlas/shared';
import { createRedshiftExport, getCrmAssignments } from '../lib/callables.js';
import { RedshiftTablePicker } from './RedshiftTablePicker.js';

interface NewRedshiftExportFormProps {
  onCreated?: (exportId: string) => void;
  /** Existing runs, used to warn early on a destination-prefix collision. */
  existingExports?: Array<RedshiftExportJob & { id: string }>;
  /** Controlled by the page so the runs log can follow the selected mode. */
  mode: RedshiftExportMode;
  onModeChange: (mode: RedshiftExportMode) => void;
}

/** Turns a callable failure into something an engineer can act on. */
function describeError(err: unknown): string {
  const message = err instanceof Error ? err.message : 'Unknown error';
  if (/permission-denied|not authorised|do not have access/i.test(message)) {
    return 'You do not have access to this app. Ask a platform admin to grant you the redshift-export app.';
  }
  return message;
}

const DEFAULT_EXCLUDE_RAW = DEFAULT_REDSHIFT_EXCLUDE_TABLES.join(', ');

/** Files-mapping mode always targets the prod uploads import area. */
export const MAPPING_BUCKET = 'prod-atlas-uploads-465358817960';

/** The two mutually exclusive export modes. */
export type RedshiftExportMode = 'csvs' | 'mapping';

export function NewRedshiftExportForm({
  onCreated,
  existingExports = [],
  mode,
  onModeChange,
}: NewRedshiftExportFormProps) {
  // Every mount now lands on the new cluster; the old one only until the
  // clients already synced there have been exported.
  const [redshiftTarget, setRedshiftTarget] = useState<RedshiftTarget>('new');
  const [schema, setSchema] = useState('');
  // A built-in slug or a custom CRM id, so plain string rather than SourceSystem.
  const [sourceSystem, setSourceSystem] = useState('');
  const [customCrms, setCustomCrms] = useState<CrmEntry[]>([]);
  const [includeSelected, setIncludeSelected] = useState<string[]>([]);
  const [includeRaw, setIncludeRaw] = useState('');
  const [excludeRaw, setExcludeRaw] = useState(DEFAULT_EXCLUDE_RAW);
  // Migration CSVs always target the default import bucket; the picker was
  // removed, so the destination is fixed rather than selectable.
  const bucket = DEFAULT_REDSHIFT_EXPORT_BUCKET;
  const [prefixRaw, setPrefixRaw] = useState('');
  const [maxFileSizeMb, setMaxFileSizeMb] = useState(100);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastResult, setLastResult] = useState<string | null>(null);
  const [justCreatedId, setJustCreatedId] = useState<string | null>(null);
  // Two mutually exclusive export modes share this form: "Migration CSVs" (the
  // default, full behaviour) and "Files mapping", which writes only the mapping
  // files to the prod uploads import area. `mode` is controlled by the page.
  const [mappingFiles, setMappingFiles] = useState<string[]>([
    ...REDSHIFT_STEMS_NOT_OFFERED,
  ]);

  // Custom CRMs are admin-managed in the settings table, so they are fetched
  // rather than imported. Failing softly keeps the built-in sources usable if
  // the call errors or an older callable is deployed that does not return
  // `crms` yet.
  useEffect(() => {
    let cancelled = false;
    getCrmAssignments()
      .then((res) => {
        if (!cancelled) setCustomCrms(res.data.crms ?? []);
      })
      .catch(() => {
        /* built-ins remain available */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const schemaProblem = schema.trim() ? redshiftSchemaError(schema) : null;
  // The picker and the free-text patterns are two ways of filling one filter,
  // so they go through the same parse as the pipeline: trimmed and de-duplicated.
  const includeTables = parseTablePatterns([...includeSelected, includeRaw].join(','));
  const excludeTables = parseTablePatterns(excludeRaw);
  const patternProblem =
    [...includeTables, ...excludeTables].map(tablePatternError).find(Boolean) ?? null;

  // Exclusions win over includes, so a table in both would be skipped without
  // the operator being told.
  const isExcluded = (table: string) =>
    excludeTables.some((pattern) => tablePatternMatches(pattern, table));
  const shadowed = includeSelected.filter(isExcluded);

  const isMapping = mode === 'mapping';
  // Files-mapping mode always writes to prod-atlas-uploads under all_files/;
  // migration CSVs use the default import bucket.
  const activeBucket = isMapping ? MAPPING_BUCKET : bucket;
  const basePrefix = redshiftExportDestination(activeBucket)?.basePrefix ?? '';
  // Resolved the same way the callable will resolve it, so the form shows the
  // key the run actually writes to. Mapping mode appends all_files/ so CSVs
  // land beside Move attachments documents
  // (e.g. 000-import/jobadder/theculletonpartnersh/all_files/).
  const resolvePrefix = isMapping
    ? resolveRedshiftMappingPrefix
    : resolveRedshiftExportPrefix;
  const prefix =
    prefixRaw.trim() || schema.trim()
      ? (resolvePrefix({
          bucket: activeBucket,
          schema,
          sourceSystem,
          ...(prefixRaw.trim() ? { path: prefixRaw } : {}),
        }) ?? '')
      : '';

  const prefixRuns = (status: (RedshiftExportJob & { id: string })['status'][]) =>
    Boolean(prefix)
      ? existingExports.filter(
          (e) =>
            e.s3Prefix === prefix &&
            e.s3Bucket === activeBucket &&
            !e.dryRun &&
            status.includes(e.status),
        )
      : [];

  // Two live runs writing the same place would clear each other's output, so any
  // live run blocks — including the one just started from this form.
  const liveRuns = prefixRuns(['queued', 'running']);
  const prefixBusy = liveRuns.length > 0;
  // The run just started here is already reported by lastResult, and calling it
  // "another run" reads as a failure. Only warn about runs launched elsewhere.
  const prefixBusyElsewhere = liveRuns.some((e) => e.id !== justCreatedId);
  // Completing does not release the path: a second run overwrites the first
  // run's files, and nothing else in the flow says so.
  const prefixHasOutput = prefixRuns(['complete']).length > 0;

  const blocked =
    loading ||
    !schema.trim() ||
    !sourceSystem ||
    Boolean(schemaProblem) ||
    (isMapping ? mappingFiles.length === 0 : Boolean(patternProblem)) ||
    prefixBusy;

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (blocked || !sourceSystem) return;

    setError(null);
    setLastResult(null);
    setJustCreatedId(null);
    setLoading(true);
    try {
      // Mapping always sends the resolved prefix (with all_files/) so the
      // callable cannot fall back to a path one folder above the attachments.
      const result = await createRedshiftExport({
        redshiftTarget,
        schema: schema.trim(),
        sourceSystem,
        includeTables: isMapping ? mappingFiles : includeTables,
        excludeTables: isMapping ? [] : excludeTables,
        s3Bucket: activeBucket,
        ...(isMapping
          ? { s3Prefix: prefix }
          : prefixRaw.trim()
            ? { s3Prefix: normalizeS3Prefix(prefixRaw) }
            : {}),
        maxFileSizeMb,
      });
      setLastResult(`Export started — output lands in ${result.data.s3Uri}`);
      setJustCreatedId(result.data.exportId);
      onCreated?.(result.data.exportId);
    } catch (err) {
      setError(describeError(err));
    } finally {
      setLoading(false);
    }
  }

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-4">
      <div className="flex flex-col gap-2">
        <span className="text-xs font-medium text-[#73726e]">Read from</span>
        <div className="flex gap-4">
          {(['new', 'legacy'] as const).map((target) => (
            <label key={target} className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={redshiftTarget === target}
                onChange={() => setRedshiftTarget(target)}
              />
              <span className="text-sm text-[#37352f]">{REDSHIFT_TARGET_LABELS[target]}</span>
            </label>
          ))}
        </div>
        {redshiftTarget === 'legacy' && (
          <span className="text-xs text-[#aeaaa4]">
            Only for clients synced to the old cluster before mounts moved to the new one.
          </span>
        )}
      </div>

      <div className="flex flex-col gap-2">
        <span className="text-xs font-medium text-[#73726e]">What to export</span>
        <div className="flex gap-4">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={!isMapping}
              onChange={() => onModeChange('csvs')}
            />
            <span className="text-sm text-[#37352f]">Migration CSVs</span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={isMapping}
              onChange={() => onModeChange('mapping')}
            />
            <span className="text-sm text-[#37352f]">Files mapping</span>
          </label>
        </div>
      </div>

      <div className="flex gap-3">
        <label className="flex-1 flex flex-col gap-1">
          <span className="text-xs font-medium text-[#73726e]">Redshift schema</span>
          <input
            required
            className={`px-2.5 py-1.5 text-sm border rounded focus:outline-none focus:ring-1 text-[#37352f] placeholder:text-[#aeaaa4] font-mono ${
              schemaProblem
                ? 'border-[#e03e3e] focus:ring-[#e03e3e]'
                : 'border-[#e9e9e7] focus:ring-[#2383e2]'
            }`}
            placeholder="e.g. acme_migrated"
            value={schema}
            onChange={(event) => setSchema(event.target.value)}
          />
          {schemaProblem ? (
            <span className="text-xs text-[#e03e3e]">{schemaProblem}</span>
          ) : (
            !isMapping &&
            prefix && (
              <span className="text-xs text-[#aeaaa4]">
                Every table in the schema is unloaded unless you filter below.
              </span>
            )
          )}
        </label>

        <label className="flex-1 flex flex-col gap-1">
          <span className="text-xs font-medium text-[#73726e]">Source system</span>
          <select
            required
            className="px-2.5 py-1.5 text-sm border border-[#e9e9e7] rounded focus:outline-none focus:ring-1 focus:ring-[#2383e2] text-[#37352f] bg-white"
            value={sourceSystem}
            onChange={(event) => setSourceSystem(event.target.value)}
          >
            <option value="" disabled>
              Select source system…
            </option>
            {SOURCE_SYSTEMS.map((source) => (
              <option key={source} value={source}>
                {SOURCE_SYSTEM_LABELS[source]}
              </option>
            ))}
            {customCrms.length > 0 && (
              <optgroup label="Added in the CRMs tab">
                {customCrms.map((crm) => (
                  <option key={crm.id} value={crm.id}>
                    {crm.name}
                  </option>
                ))}
              </optgroup>
            )}
          </select>
          <span className="text-xs text-[#aeaaa4]">
            The CRM this schema was migrated from. Leads the destination path.
          </span>
        </label>
      </div>

      {isMapping && (
        <>
          <div className="flex flex-col gap-1">
            <span className="text-xs font-medium text-[#73726e]">Mapping files</span>
            {REDSHIFT_STEMS_NOT_OFFERED.map((name) => (
              <label key={name} className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={mappingFiles.includes(name)}
                  onChange={(event) =>
                    setMappingFiles((prev) =>
                      event.target.checked
                        ? [...prev, name]
                        : prev.filter((f) => f !== name),
                    )
                  }
                />
                <code className="font-mono text-sm text-[#37352f]">{name}</code>
              </label>
            ))}
            {mappingFiles.length === 0 && (
              <span className="text-xs text-[#e03e3e]">
                Select at least one mapping file.
              </span>
            )}
          </div>

          <div className="flex flex-col gap-1">
            <span className="text-xs font-medium text-[#73726e]">Destination</span>
            <div className="flex gap-2">
              <div
                className="px-2 py-1.5 text-sm border border-[#e9e9e7] rounded text-[#73726e] font-mono bg-[#f7f7f5] whitespace-nowrap"
                title="The bucket is fixed for files mapping."
              >
                {MAPPING_BUCKET}/{basePrefix}
              </div>
              <input
                className="flex-1 px-2.5 py-1.5 text-sm border border-[#e9e9e7] rounded focus:outline-none focus:ring-1 focus:ring-[#2383e2] text-[#37352f] placeholder:text-[#aeaaa4] font-mono"
                placeholder={
                  schema.trim()
                    ? `${buildRedshiftExportPrefix(schema, sourceSystem)}all_files/`
                    : 'source/schema/all_files/'
                }
                value={prefixRaw}
                onChange={(event) => setPrefixRaw(event.target.value)}
              />
            </div>
            <span className="text-xs text-[#aeaaa4]">
              {prefix ? (
                <>
                  Output lands in{' '}
                  <code className="font-mono">
                    s3://{activeBucket}/{prefix}
                  </code>
                  . Everything for this bucket stays under{' '}
                  <code className="font-mono">{basePrefix}</code>.
                </>
              ) : (
                'Leave the path blank to use the source and schema.'
              )}
            </span>
          </div>
        </>
      )}

      {!isMapping && (
        <>
      <div className="flex flex-col gap-1">
        <span className="text-xs font-medium text-[#73726e]">Only these tables</span>
        <RedshiftTablePicker
          value={includeSelected}
          onChange={setIncludeSelected}
          options={REDSHIFT_STANDARD_TABLES}
          isExcluded={isExcluded}
        />
        <span className="text-xs text-[#aeaaa4]">
          Leave empty to export every table in the schema. Each option is the
          canonical name — <code className="font-mono">people</code> also matches
          the CRM-suffixed <code className="font-mono">people_vincere</code>.{' '}
          <span className="relative inline-block group">
            <button
              type="button"
              className="underline decoration-dotted cursor-help hover:text-[#37352f]"
            >
              All {REDSHIFT_STANDARD_TABLES.length} standard names
            </button>
            {/* Padding rather than a margin keeps the hover area contiguous, so
                the list does not vanish as the pointer travels down to it. */}
            <span className="invisible group-hover:visible group-focus-within:visible absolute left-0 top-full z-30 pt-1">
              <span className="block w-[26rem] max-w-[80vw] columns-2 gap-3 rounded border border-[#e9e9e7] bg-white p-2.5 shadow-lg">
                {REDSHIFT_STANDARD_TABLES.map((table) => (
                  <span
                    key={table}
                    className={`block truncate font-mono text-[11px] leading-5 ${
                      includeSelected.includes(table)
                        ? 'text-[#0f7b6c]'
                        : 'text-[#37352f]'
                    }`}
                  >
                    {table}
                  </span>
                ))}
              </span>
            </span>
          </span>
        </span>
      </div>

      <label className="flex flex-col gap-1">
        <span className="text-xs font-medium text-[#73726e]">
          …or type table names, comma-separated
        </span>
        <input
          className="px-2.5 py-1.5 text-sm border border-[#e9e9e7] rounded focus:outline-none focus:ring-1 focus:ring-[#2383e2] text-[#37352f] placeholder:text-[#aeaaa4] font-mono"
          placeholder="e.g. legacy_notes, opportunit*, custom_*"
          value={includeRaw}
          onChange={(event) => setIncludeRaw(event.target.value)}
        />
        <span className="text-xs text-[#aeaaa4]">
          For tables that are not standard names. Globs allowed. Adds to anything
          picked above rather than replacing it
          {includeTables.length > 0 && (
            <>
              , so this run covers{' '}
              <span className="text-[#73726e]">
                {includeTables.length} {includeTables.length === 1 ? 'entry' : 'entries'}
              </span>
            </>
          )}
          .
        </span>
      </label>

      <div className="flex flex-col gap-1">
        <span className="text-xs font-medium text-[#73726e]">Destination</span>
        <div className="flex gap-2">
          <div
            className="px-2 py-1.5 text-sm border border-[#e9e9e7] rounded text-[#73726e] font-mono bg-[#f7f7f5] whitespace-nowrap"
            title="The destination bucket is fixed."
          >
            {bucket}/{basePrefix}
          </div>
          <input
            className="flex-1 px-2.5 py-1.5 text-sm border border-[#e9e9e7] rounded focus:outline-none focus:ring-1 focus:ring-[#2383e2] text-[#37352f] placeholder:text-[#aeaaa4] font-mono"
            placeholder={
              schema.trim()
                ? buildRedshiftExportPrefix(schema, sourceSystem)
                : 'source/schema/'
            }
            value={prefixRaw}
            onChange={(event) => setPrefixRaw(event.target.value)}
          />
        </div>
        <span className="text-xs text-[#aeaaa4]">
          {prefix ? (
            <>
              Output lands in <code className="font-mono">s3://{bucket}/{prefix}</code>
              {basePrefix && (
                <>
                  . Everything for this bucket stays under{' '}
                  <code className="font-mono">{basePrefix}</code>
                </>
              )}
              .
            </>
          ) : (
            'Leave the path blank to use the schema name.'
          )}
        </span>
      </div>

      {showAdvanced ? (
        <>
          <label className="flex flex-col gap-1">
            <span className="text-xs font-medium text-[#73726e]">
              Skip these tables (optional, comma-separated)
            </span>
            <input
              className="px-2.5 py-1.5 text-sm border border-[#e9e9e7] rounded focus:outline-none focus:ring-1 focus:ring-[#2383e2] text-[#37352f] placeholder:text-[#aeaaa4] font-mono"
              placeholder="e.g. audit_*, _temp_backfill"
              value={excludeRaw}
              onChange={(event) => setExcludeRaw(event.target.value)}
            />
            <span className="text-xs text-[#aeaaa4]">
              Pre-filled with the migration scratch and mapping tables. Edit or
              clear it if a run needs one of them.{' '}
              {excludeRaw.trim() !== DEFAULT_EXCLUDE_RAW && (
                <button
                  type="button"
                  onClick={() => setExcludeRaw(DEFAULT_EXCLUDE_RAW)}
                  className="underline hover:text-[#37352f]"
                >
                  Restore defaults
                </button>
              )}
            </span>
          </label>

          <label className="w-40 flex flex-col gap-1">
            <span className="text-xs font-medium text-[#73726e]">
              Max file size (MB)
            </span>
            <input
              type="number"
              min={5}
              max={6144}
              className="px-2.5 py-1.5 text-sm border border-[#e9e9e7] rounded focus:outline-none focus:ring-1 focus:ring-[#2383e2] text-[#37352f] tabular-nums"
              value={maxFileSizeMb}
              onChange={(event) => setMaxFileSizeMb(Number(event.target.value))}
            />
          </label>
        </>
      ) : (
        <button
          type="button"
          onClick={() => setShowAdvanced(true)}
          className="self-start text-xs text-[#73726e] hover:text-[#37352f] underline"
        >
          {excludeTables.length > 0
            ? `Skipping ${excludeTables.length} table patterns — exclusions, destination and file size`
            : 'Exclusions, destination and file size'}
        </button>
      )}
        </>
      )}

      {!isMapping && patternProblem && (
        <p className="text-sm text-[#e03e3e] bg-[#fbe8e8] border border-[#f5c6c6] rounded px-3 py-2">
          {patternProblem}
        </p>
      )}

      {!isMapping && shadowed.length > 0 && (
        <p className="text-sm text-[#b5820b] bg-[#fbf3db] border border-[#f2e2b4] rounded px-3 py-2">
          {shadowed.join(', ')} {shadowed.length === 1 ? 'is' : 'are'} also matched
          by an exclusion, which wins — {shadowed.length === 1 ? 'it' : 'they'} will
          not be exported. Remove the pattern under the advanced options to export{' '}
          {shadowed.length === 1 ? 'it' : 'them'}.
        </p>
      )}

      {prefixBusyElsewhere && (
        <p className="text-sm text-[#b5820b] bg-[#fbf3db] border border-[#f2e2b4] rounded px-3 py-2">
          A run is currently writing to{' '}
          <code className="font-mono">s3://{activeBucket}/{prefix}</code>. Wait for it
          to finish, or choose a different path.
        </p>
      )}

      {prefixHasOutput && !prefixBusy && (
        <p className="text-sm text-[#b5820b] bg-[#fbf3db] border border-[#f2e2b4] rounded px-3 py-2">
          An earlier run already wrote output to{' '}
          <code className="font-mono">s3://{activeBucket}/{prefix}</code>. Starting this
          export overwrites those files.
        </p>
      )}

      {error && (
        <p className="text-sm text-[#e03e3e] bg-[#fbe8e8] border border-[#f5c6c6] rounded px-3 py-2">
          {error}
        </p>
      )}

      {lastResult && (
        <p className="text-sm text-[#0f7b6c] bg-[#e5f5f2] border border-[#c5e8e1] rounded px-3 py-2">
          {lastResult}
        </p>
      )}

      <div className="flex justify-end pt-1">
        <button
          type="submit"
          disabled={blocked}
          className="px-4 py-1.5 text-sm font-medium text-white bg-[#37352f] rounded hover:bg-[#2e2e2c] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {loading ? 'Starting…' : 'Start export'}
        </button>
      </div>
    </form>
  );
}
